import { ApiService } from '../ApiService';
import type { JournalEntry } from '../../../types/accounting';
import type { BusinessUnitFilterBase } from '../../../types/filters';
import type { PaginatedResponse } from '../types';

/**
 * Interface de paramètres pour les requêtes d'écritures comptables
 * Supporte le filtrage par unité commerciale (entreprise/succursale/point de vente)
 */
export interface JournalEntryParams extends BusinessUnitFilterBase {
  page?: number;
  pageSize?: number;
  fiscalYearId?: string;
  status?: 'draft' | 'pending' | 'approved' | 'posted' | 'cancelled' | 'rejected';
  journalType?: 'sales' | 'purchases' | 'bank' | 'cash' | 'general' | 'inventory' | 'financial' | 'miscellaneous';
  startDate?: string;
  endDate?: string;
  search?: string;
}

/**
 * Résultat de création en lot
 */
export interface BatchCreateResult {
  success: boolean;
  data: {
    created: number;
    entries: JournalEntry[];
    failed?: Array<{ index: number; error: string }>;
  };
}

/**
 * Résultat de mise à jour en lot
 */
export interface BatchUpdateResult {
  success: boolean;
  data: {
    updated: number;
    entries?: JournalEntry[];
    failed?: Array<{ id: string; error: string }>;
  };
}

/**
 * Résultat de suppression en lot
 */
export interface BatchDeleteResult {
  success: boolean;
  data: {
    deleted: number;
    failed?: Array<{ id: string; error: string }>;
  };
}

/**
 * Résultat d'action en lot (submit, approve, reject, post)
 */
export interface BatchActionResult {
  success: boolean;
  data: {
    processed: number;
    entries?: JournalEntry[];
    errors?: Array<{ id: string; error: string }>;
  };
}

/**
 * Options pour les opérations en lot
 */
export interface BatchOptions {
  stopOnError?: boolean;  // Si true, rollback toutes les opérations à la première erreur
}

/**
 * Normalise `journalType` entre les conventions front (clés JOURNAL_TYPES
 * en MAJUSCULES, p.ex. "SALES", "PURCHASE", "GENERAL") et la convention
 * backend SYSCOHADA (valeurs d'enum en minuscules, "sales", "purchases",
 * "general"). Sans ce mapping le backend retourne 400 :
 *   "journalType must be one of: general, sales, purchases, bank, cash"
 * et SyncService archive l'item dans sync_failures → écriture jamais
 * persistée côté DB.
 */
const JOURNAL_TYPE_TO_BACKEND: Record<string, string> = {
  SALES: 'sales',
  PURCHASE: 'purchases',  // attention : pluriel côté backend
  PURCHASES: 'purchases',
  BANK: 'bank',
  CASH: 'cash',
  GENERAL: 'general',
  // déjà en minuscules : pass-through
  sales: 'sales',
  purchases: 'purchases',
  bank: 'bank',
  cash: 'cash',
  general: 'general',
};

function normalizeForBackend<T extends Partial<JournalEntry>>(entry: T): T {
  const out: any = { ...entry };
  if (out.journalType && JOURNAL_TYPE_TO_BACKEND[out.journalType]) {
    out.journalType = JOURNAL_TYPE_TO_BACKEND[out.journalType];
  }
  // Le DTO backend a un alias historique `type` — on aligne pareil au cas où
  if (out.type && JOURNAL_TYPE_TO_BACKEND[out.type]) {
    out.type = JOURNAL_TYPE_TO_BACKEND[out.type];
  }
  return out;
}

export const journalEntriesApi = {
  /**
   * Récupérer toutes les écritures comptables
   * @see GET /journal-entries
   */
  getAll: (params?: JournalEntryParams) =>
    ApiService.get<PaginatedResponse<JournalEntry>>('/journal-entries', params),

  /**
   * Récupérer une écriture comptable par ID
   * @see GET /journal-entries/:id
   */
  getById: (id: string) =>
    ApiService.get<JournalEntry>(`/journal-entries/${id}`),

  /**
   * Créer une nouvelle écriture comptable
   * @see POST /journal-entries
   *
   * Règles de validation:
   * - totalDebit doit être égal à totalCredit (tolérance 0.001)
   * - Minimum 2 lignes requises
   * - Une ligne ne peut pas avoir débit > 0 ET crédit > 0
   * - Montants non négatifs
   * - Comptes valides
   * - Exercice ouvert
   * - Date dans la période de l'exercice
   */
  create: (entry: Partial<JournalEntry> & BusinessUnitFilterBase) =>
    ApiService.post<JournalEntry>('/journal-entries', normalizeForBackend(entry)),

  /**
   * Mettre à jour une écriture comptable
   * @see PUT /journal-entries/:id
   * Note: Seules les écritures en statut 'draft' peuvent être modifiées
   */
  update: (id: string, entry: Partial<JournalEntry>) => 
    ApiService.put<JournalEntry>(`/journal-entries/${id}`, entry),

  /**
   * Supprimer une écriture comptable
   * @see DELETE /journal-entries/:id
   * Note: Seules les écritures en statut 'draft' peuvent être supprimées
   */
  delete: (id: string) => 
    ApiService.delete<void>(`/journal-entries/${id}`),

  // ============================================
  // TRANSITIONS D'ÉTAT
  // ============================================

  /**
   * Soumettre une écriture pour approbation (draft → pending)
   * @see POST /journal-entries/:id/submit
   */
  submit: (id: string) => 
    ApiService.post<{ success: boolean; data: JournalEntry }>(`/journal-entries/${id}/submit`, {}),

  /**
   * Approuver une écriture en attente (pending → approved)
   * @see POST /journal-entries/:id/approve
   */
  approve: (id: string) => 
    ApiService.post<{ success: boolean; data: JournalEntry }>(`/journal-entries/${id}/approve`, {}),

  /**
   * Rejeter une écriture en attente
   * @see POST /journal-entries/:id/reject
   */
  reject: (id: string, reason: string) => 
    ApiService.post<{ success: boolean; data: JournalEntry }>(`/journal-entries/${id}/reject`, { reason }),

  /**
   * Comptabiliser une écriture approuvée (approved → posted)
   * @see POST /journal-entries/:id/post
   */
  post: (id: string) => 
    ApiService.post<{ success: boolean; data: JournalEntry }>(`/journal-entries/${id}/post`, {}),

  /**
   * Annuler une écriture comptabilisée (crée une contre-passation)
   * @see POST /journal-entries/:id/cancel
   */
  cancel: (id: string, reason: string) => 
    ApiService.post<{ success: boolean; data: JournalEntry; reversalEntry: JournalEntry }>(`/journal-entries/${id}/cancel`, { reason }),

  /**
   * Extourner une écriture comptabilisée (génère une écriture miroir en DRAFT)
   * Conforme SYSCOHADA — débit ↔ crédit inversés, liens bidirectionnels
   * @see POST /journal-entries/:id/reverse
   * 
   * Seules les écritures au statut POSTED peuvent être extournées.
   * L'écriture générée est créée en DRAFT.
   * L'originale reçoit reversedBy, la nouvelle a reversalOf.
   */
  reverse: (id: string, data?: { reversalDate?: string; description?: string }) => 
    ApiService.post<{ success: boolean; data: JournalEntry }>(`/journal-entries/${id}/reverse`, data || {}),

  // ============================================
  // ACTIONS EN LOT (TRANSACTIONNELLES)
  // ============================================

  /**
   * Créer plusieurs écritures comptables en lot (transactionnel)
   * @see POST /journal-entries/batch/create
   * 
   * @param journals - Tableau des écritures à créer
   * @param options - Options de création (stopOnError pour rollback complet)
   */
  batchCreate: (journals: Array<Partial<JournalEntry> & BusinessUnitFilterBase>, options?: BatchOptions) => {
    const queryParams = options?.stopOnError ? '?stopOnError=true' : '';
    return ApiService.post<BatchCreateResult>(`/journal-entries/batch/create${queryParams}`, { journals });
  },

  /**
   * Mettre à jour plusieurs écritures comptables (transactionnel)
   * @see PUT /journal-entries/batch/update
   * 
   * @param updates - Tableau des mises à jour avec id et données
   * @param options - Options (stopOnError pour rollback complet)
   */
  batchUpdate: (updates: Array<{ id: string; data: Partial<JournalEntry> }>, options?: BatchOptions) => {
    const queryParams = options?.stopOnError ? '?stopOnError=true' : '';
    return ApiService.put<BatchUpdateResult>(`/journal-entries/batch/update${queryParams}`, { updates });
  },

  /**
   * Supprimer plusieurs écritures en brouillon (transactionnel)
   * @see DELETE /journal-entries/batch/delete
   * 
   * @param ids - IDs des écritures à supprimer
   * @param options - Options (stopOnError pour rollback complet)
   */
  batchDelete: (ids: string[], options?: BatchOptions) => {
    const queryParams = options?.stopOnError ? '?stopOnError=true' : '';
    return ApiService.post<BatchDeleteResult>(`/journal-entries/batch/delete${queryParams}`, { ids });
  },

  /**
   * Soumettre plusieurs écritures pour approbation
   * @see PUT /journal-entries/batch/status
   */
  submitBatch: (ids: string[]) => 
    ApiService.put<BatchActionResult>('/journal-entries/batch/status', { ids, status: 'pending' }),

  /**
   * Approuver plusieurs écritures en attente
   * @see PUT /journal-entries/batch/status
   */
  approveBatch: (ids: string[]) => 
    ApiService.put<BatchActionResult>('/journal-entries/batch/status', { ids, status: 'approved' }),

  /**
   * Rejeter plusieurs écritures en attente
   * @see PUT /journal-entries/batch/status
   */
  rejectBatch: (ids: string[], reason: string) => 
    ApiService.put<BatchActionResult>('/journal-entries/batch/status', { ids, status: 'rejected', rejectionReason: reason }),

  /**
   * Comptabiliser plusieurs écritures approuvées
   * @see PUT /journal-entries/batch/status
   */
  postBatch: (ids: string[]) => 
    ApiService.put<BatchActionResult>('/journal-entries/batch/status', { ids, status: 'posted' }),

  // ============================================
  // MÉTHODES DÉPRÉCIÉES (pour compatibilité)
  // ============================================

  /**
   * @deprecated Utiliser batchDelete à la place
   */
  deleteBatch: (ids: string[]) => 
    ApiService.post<{ success: boolean; data: { deleted: number; errors?: Array<{ id: string; error: string }> } }>(
      '/journal-entries/batch/delete', 
      { ids }
    )
};